// App-wide toast notifications. `toast()` can be called from anywhere (also outside React).
import { useEffect, useSyncExternalStore } from 'react';
import { Icon, type IconName } from './Icon';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface ToastOptions {
  kind?: ToastKind;
  /** Auto-dismiss delay; 0 keeps the toast until it is closed. */
  durationMs?: number;
  action?: { label: string; onClick: () => void };
  /** The message shows the user's content (e.g. a reminder's text): removed when the session ends. */
  private?: boolean;
}

interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
  durationMs: number;
  action?: { label: string; onClick: () => void };
  private: boolean;
}

const MAX_VISIBLE = 4;
let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of [...listeners]) listener();
}

export function toast(message: string, opts: ToastOptions = {}): number {
  const kind = opts.kind ?? 'info';
  const id = nextId++;
  // Identical messages in quick succession (e.g. repeated failures) are shown once.
  items = items.filter((t) => t.message !== message);
  const item: ToastItem = { id, message, kind, durationMs: opts.durationMs ?? (kind === 'error' ? 8_000 : 4_500), private: opts.private ?? false };
  if (opts.action) item.action = opts.action;
  items = [...items, item].slice(-MAX_VISIBLE);
  emit();
  return id;
}

export function dismissToast(id: number): void {
  const before = items.length;
  items = items.filter((t) => t.id !== id);
  if (items.length !== before) emit();
}

/** Removes every toast (e.g. when the signed-in user changes: messages about their work go too). */
export function dismissAllToasts(): void {
  if (items.length === 0) return;
  items = [];
  emit();
}

/** Removes the toasts that show the user's content (the session ended: the login shows nothing of it). */
export function dismissPrivateToasts(): void {
  const before = items.length;
  items = items.filter((t) => !t.private);
  if (items.length !== before) emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getItems = () => items;

const KIND_ICON: Record<ToastKind, IconName> = {
  info: 'info',
  success: 'check',
  warning: 'alert',
  error: 'alert',
};

function ToastView({ item }: { item: ToastItem }) {
  useEffect(() => {
    if (item.durationMs <= 0) return;
    const timer = setTimeout(() => dismissToast(item.id), item.durationMs);
    return () => clearTimeout(timer);
  }, [item.id, item.durationMs]);

  return (
    <div className={`toast toast--${item.kind}`} role={item.kind === 'error' ? 'alert' : 'status'}>
      <Icon name={KIND_ICON[item.kind]} size={20} className="toast__icon" />
      <span className="toast__message">{item.message}</span>
      {item.action && (
        <button
          type="button"
          className="toast__action"
          onClick={() => {
            item.action?.onClick();
            dismissToast(item.id);
          }}
        >
          {item.action.label}
        </button>
      )}
      <button type="button" className="toast__close" aria-label="Zapri obvestilo" onClick={() => dismissToast(item.id)}>
        <Icon name="close" size={18} />
      </button>
    </div>
  );
}

export function ToastHost() {
  const list = useSyncExternalStore(subscribe, getItems, getItems);
  return (
    <div className="toasts" aria-live="polite" aria-relevant="additions">
      {list.map((item) => (
        <ToastView key={item.id} item={item} />
      ))}
    </div>
  );
}
